<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import FormFieldCharacterCount from './form-field-character-count.vue';

defineOptions({
    inheritAttrs: false,
});

interface Props {
    labelId: string;
    label: string;
    required: boolean;
    errorMessage: string;
    description?: string;
    // Shows a `count/maxLength` counter at the end of the message row when both are set.
    characterCount?: number;
    maxLength?: number;
}

const {
    labelId,
    label,
    required,
    errorMessage,
    description = undefined,
    characterCount = undefined,
    maxLength = undefined,
} = defineProps<Props>();
const showCharacterCount = computed(() => characterCount !== undefined && maxLength !== undefined);
const attrs = useAttrs();
const reservedErrorText = computed(() => errorMessage || '\u00A0');
const hasDescription = computed(() => Boolean(description?.trim()));
const ERROR_DESCRIPTION_TOOLTIP_THRESHOLD = 120;
const showDescriptionTooltip = computed(() =>
    Boolean(hasDescription.value && description && description.trim().length > ERROR_DESCRIPTION_TOOLTIP_THRESHOLD),
);
const showInlineErrorDescription = computed(() =>
    Boolean(errorMessage && hasDescription.value && !showDescriptionTooltip.value),
);
const isDescriptionTooltipOpen = ref(false);

function openDescriptionTooltip() {
    if (!showDescriptionTooltip.value) return;
    isDescriptionTooltipOpen.value = true;
}

function closeDescriptionTooltip() {
    isDescriptionTooltipOpen.value = false;
}

function onDescriptionTooltipFocusOut(event: FocusEvent) {
    const currentTarget = event.currentTarget;
    const relatedTarget = event.relatedTarget;

    if (!(currentTarget instanceof HTMLElement)) {
        closeDescriptionTooltip();
        return;
    }

    if (relatedTarget instanceof Node && currentTarget.contains(relatedTarget)) return;
    closeDescriptionTooltip();
}
</script>

<template>
    <TooltipProvider v-if="showDescriptionTooltip">
        <Tooltip :open="isDescriptionTooltipOpen" :delay-duration="0">
            <TooltipTrigger as-child>
                <div
                    v-bind="attrs"
                    data-id="error-description-trigger"
                    class="input-group group"
                    @mouseenter="openDescriptionTooltip"
                    @mouseleave="closeDescriptionTooltip"
                    @focusin="openDescriptionTooltip"
                    @focusout="onDescriptionTooltipFocusOut"
                >
                    <label :for="labelId" :class="{ required: required }">{{ label }}</label>
                    <div class="input-wrapper bg-lightest" :class="{ 'has-error': !!errorMessage }">
                        <slot />
                    </div>
                    <div data-id="error-message" class="relative mt-1 flex items-start gap-sm text-sm">
                        <div data-id="primary-message-slot" class="min-h-5 w-full min-w-0 flex-1">
                            <p
                                v-if="errorMessage"
                                data-id="field-message"
                                aria-hidden="false"
                                class="error m-0 w-full leading-5"
                            >
                                {{ errorMessage }}
                            </p>
                            <p
                                v-else
                                :aria-hidden="errorMessage ? 'false' : 'true'"
                                class="error m-0 w-full leading-5"
                                :class="{ invisible: !errorMessage }"
                            >
                                {{ reservedErrorText }}
                            </p>
                        </div>
                        <FormFieldCharacterCount
                            v-if="showCharacterCount"
                            :id="`${labelId}-character-count`"
                            :count="characterCount ?? 0"
                            :max-length="maxLength ?? 0"
                        />
                    </div>
                </div>
            </TooltipTrigger>
            <TooltipContent
                data-id="helper-tooltip"
                side="top"
                align="start"
                :side-offset="4"
                class="max-w-[min(28rem,calc(100vw-2rem))] text-left break-words whitespace-normal"
            >
                {{ description }}
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
    <div v-else v-bind="attrs" class="input-group group">
        <label :for="labelId" :class="{ required: required }">{{ label }}</label>
        <div class="input-wrapper bg-lightest" :class="{ 'has-error': !!errorMessage }">
            <slot />
        </div>
        <div data-id="error-message" class="relative mt-1 flex items-start gap-sm text-sm">
            <div data-id="primary-message-slot" class="min-h-5 w-full min-w-0 flex-1">
                <p v-if="errorMessage" data-id="field-message" aria-hidden="false" class="error m-0 w-full leading-5">
                    {{ errorMessage }}
                </p>
                <p
                    v-else-if="hasDescription"
                    data-id="helper-message"
                    class="incident-field-helper m-0 w-full text-sm leading-5 text-gray-300 opacity-0 transition-opacity duration-150 ease-in-out group-focus-within:opacity-100 group-hover:opacity-100"
                >
                    {{ description }}
                </p>
                <p
                    v-else
                    :aria-hidden="errorMessage ? 'false' : 'true'"
                    class="error m-0 w-full leading-5"
                    :class="{ invisible: !errorMessage }"
                >
                    {{ reservedErrorText }}
                </p>
            </div>
            <FormFieldCharacterCount
                v-if="showCharacterCount"
                :id="`${labelId}-character-count`"
                :count="characterCount ?? 0"
                :max-length="maxLength ?? 0"
            />
            <p
                v-if="showInlineErrorDescription"
                data-id="helper-message"
                class="incident-field-helper pointer-events-none absolute top-full left-0 z-10 m-0 w-full -translate-y-1 text-sm leading-5 text-gray-300 opacity-0 transition-opacity duration-150 ease-in-out group-focus-within:opacity-100 group-hover:opacity-100"
            >
                {{ description }}
            </p>
        </div>
    </div>
</template>

<style scoped>
@reference '@/assets/styles/default.css';

label {
    display: flex;
    align-items: center;
    margin-bottom: var(--spacing-xs);
}

.required::before {
    content: '*';
    margin-right: var(--spacing-xs);
    color: var(--color-error);
    display: flex;
    align-items: center;
}
</style>
